import { useMemo, useState } from "react";

import { api } from "../api/client";
import type { CallerId, CallerIdCreated } from "../api/types";
import { failureMessage } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { ActionError, Badge, Button, Card, Confirm, Field, Modal, PageHeader, inputClass } from "../components/ui";
import { formatDateTime } from "./Calls";
import { useSession } from "../store/session";

// Caller IDs: los números que la cuenta puede presentar como origen.
//
// La verificación es por llamada: la plataforma marca al número, dice un código
// de seis dígitos y quien contesta lo teclea aquí. Eso prueba que la cuenta
// controla el número, que es lo único que impide presentar el número de un
// tercero.
//
// El código vive 15 minutos y admite 5 intentos (internal/numbers/handlers.go:
// `maxVerificationAttempts`); agotados, hay que volver a registrar el número
// para que se genere uno nuevo. Esta pantalla lo dice en lugar de dejar que la
// persona lo descubra con un 429.

export function CallerIds() {
  const callerIds = usePagedResource<CallerId>("/api/v1/caller-ids");
  const session = useSession((st) => st.session);
  const can = useSession((st) => st.can);

  // Aprobar sin llamada es de plataforma: saltarse la prueba de titularidad
  // de un número no es algo que deba poder hacer el dueño de la cuenta sobre
  // sí mismo. El botón solo aparece para quien además administra cuentas.
  const puedeAprobar = can("tenants.manage");
  const tenantActivo = session?.active_tenant_id ?? "";

  const [registering, setRegistering] = useState(false);
  const [verifying, setVerifying] = useState<CallerId | null>(null);
  const [deleting, setDeleting] = useState<CallerId | null>(null);
  const [approving, setApproving] = useState<CallerId | null>(null);
  const [notice, setNotice] = useState<CallerIdCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function approve(callerId: CallerId) {
    setActionError(null);
    setBusy(true);
    try {
      await api.post(
        `/api/v1/tenants/${tenantActivo}/caller-ids/${callerId.id}/approve`,
      );
      setApproving(null);
      await callerIds.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo aprobar el caller id"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(callerId: CallerId) {
    setActionError(null);
    setBusy(true);
    try {
      await api.delete(`/api/v1/caller-ids/${callerId.id}`);
      setDeleting(null);
      await callerIds.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo eliminar el caller ID"));
    } finally {
      setBusy(false);
    }
  }

  const columnas = useMemo<ColumnDef<CallerId, any>[]>(
    () => [
      {
        id: "e164",
        accessorKey: "e164",
        header: "Número",
        meta: { label: "Número" },
        cell: ({ row }) => <span className="font-mono text-slate-100">{row.original.e164}</span>,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado" },
        cell: ({ row }) => (
          <Badge tone={STATUS_TONES[row.original.status]}>
            {STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: "verified_at",
        accessorKey: "verified_at",
        header: "Verificado",
        meta: { label: "Verificado", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.verified_at)}</span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Registrado",
        meta: { label: "Registrado", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
    ],
    [],
  );

  const acciones = useMemo<DataTableRowAction<CallerId>[]>(
    () => [
      {
        label: "Verificar",
        hidden: (callerId) => callerId.status === "verified",
        onSelect: (callerId) => setVerifying(callerId),
      },
      {
        // Saltarse la prueba de titularidad es de plataforma: un cliente no
        // se aprueba sus propios números.
        label: "Aprobar sin llamada",
        hidden: (callerId) =>
          callerId.status === "verified" || !puedeAprobar || tenantActivo === "",
        onSelect: (callerId) => setApproving(callerId),
      },
      { label: "Eliminar", tone: "danger", onSelect: (callerId) => setDeleting(callerId) },
    ],
    [puedeAprobar, tenantActivo],
  );

  return (
    <div>
      <PageHeader
        title="Caller IDs"
        description="Números que esta cuenta puede presentar como origen. Solo se admite un caller ID verificado: presentar un número ajeno es suplantación, y la plataforma no lo permite."
        action={!registering && <Button onClick={() => setRegistering(true)}>Registrar número</Button>}
      />

      {actionError !== null && <ActionError message={actionError} />}

      {notice !== null && (
        <Card
          className={`mb-6 p-4 ${
            notice.verification_call_queued
              ? "border-emerald-500/30 bg-emerald-500/10"
              : "border-amber-500/30 bg-amber-500/10"
          }`}
        >
          {notice.verification_call_queued ? (
            <>
              <p className="text-sm font-semibold text-emerald-200">
                Llamada de verificación en cola a {notice.e164}
              </p>
              <p className="mt-1 text-sm text-emerald-200">
                Contesta y escucha el código de seis dígitos; luego tecléalo en «Verificar». El
                código caduca en 15 minutos.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-amber-200">
                {notice.e164} quedó registrado, pero <strong>no se pudo encolar la llamada</strong>
              </p>
              <p className="mt-1 text-sm text-amber-200">
                El número está en la lista en estado pendiente y nadie va a recibir el código.
                Elimínalo y vuelve a registrarlo para reintentar la llamada; si vuelve a fallar,
                revisa saldo, troncal y nodos.
              </p>
            </>
          )}
          <div className="mt-3">
            <Button variant="secondary" onClick={() => setNotice(null)}>
              Entendido
            </Button>
          </div>
        </Card>
      )}

      {registering && (
        <RegisterForm
          onCancel={() => setRegistering(false)}
          onCreated={(created) => {
            setRegistering(false);
            setNotice(created);
            void callerIds.reload(true);
          }}
        />
      )}

      {approving !== null && (
        <Confirm
          title={`Aprobar ${approving.e164} sin llamada de verificación`}
          description="Queda verificado al instante, sin comprobar que la cuenta controla el número. Hazlo solo si la titularidad te consta por otra vía: presentarse con el número de otro es suplantación. La aprobación queda en el audit log a tu nombre."
          confirmLabel="Aprobar"
          busy={busy}
          onConfirm={() => void approve(approving)}
          onCancel={() => setApproving(null)}
        />
      )}

      {verifying !== null && (
        <VerifyForm
          callerId={verifying}
          onCancel={() => setVerifying(null)}
          onVerified={() => {
            setVerifying(null);
            void callerIds.reload(true);
          }}
        />
      )}

      {deleting !== null && (
        <Confirm
          title={`Eliminar ${deleting.e164}`}
          description={
            deleting.status === "verified"
              ? "Las llamadas que presenten este número como origen empezarán a ser rechazadas. Volver a registrarlo exige verificarlo de nuevo por llamada."
              : "Se elimina el registro pendiente y el código deja de servir. Registrarlo otra vez genera un código nuevo y una llamada nueva."
          }
          confirmLabel="Eliminar"
          busy={busy}
          onConfirm={() => void remove(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}

      <DataTable<CallerId>
        data={callerIds.data}
        columns={columnas}
        getRowId={(callerId) => callerId.id}
        loading={callerIds.loading}
        error={callerIds.error}
        onRetry={() => void callerIds.reload(false)}
        caption="Caller IDs de la cuenta"
        empty={{
          title: "Sin caller IDs",
          description:
            "Para marcar hace falta un número propio o un caller ID verificado.",
        }}
        search={{ placeholder: "Buscar número…" }}
        filters={[
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "verified", label: "Verificado" },
              { value: "pending", label: "Pendiente" },
              { value: "rejected", label: "Rechazado" },
            ],
          },
        ]}
        rowActions={acciones}
        urlStateKey="callerids"
        pagination={{
          pageSize: callerIds.enEstaPagina,
          hasNext: callerIds.hayMas,
          hasPrevious: callerIds.hayAnterior,
          onNext: callerIds.siguiente,
          onPrevious: callerIds.anterior,
        }}
      />
    </div>
  );
}

const STATUS_LABELS: Record<CallerId["status"], string> = {
  pending: "Pendiente",
  verified: "Verificado",
  rejected: "Rechazado",
};

const STATUS_TONES: Record<CallerId["status"], "neutral" | "good" | "warn" | "bad"> = {
  pending: "warn",
  verified: "good",
  rejected: "bad",
};

function RegisterForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (created: CallerIdCreated) => void;
}) {
  const [e164, setE164] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      onCreated(await api.post<CallerIdCreated>("/api/v1/caller-ids", { e164 }));
    } catch (err) {
      setError(failureMessage(err, "no se pudo registrar el número"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Registrar un caller ID"
      description="La plataforma marcará a ese número y te dictará un código."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="Número en formato E.164"
          hint="Con signo de más y código de país, sin espacios ni guiones. Al registrarlo, la plataforma marca a ese número y dicta el código."
          error={error ?? undefined}
        >
          <input
            className={inputClass}
            required
            inputMode="tel"
            placeholder="+528112345678"
            value={e164}
            onChange={(e) => setE164(e.target.value.trim())}
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Registrando…" : "Registrar y llamar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function VerifyForm({
  callerId,
  onCancel,
  onVerified,
}: {
  callerId: CallerId;
  onCancel: () => void;
  onVerified: () => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/v1/caller-ids/${callerId.id}/verify`, { code });
      onVerified();
    } catch (err) {
      setError(failureMessage(err, "no se pudo verificar el código"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Verificar ${callerId.e164}`}
      description="La plataforma te dictó un código por teléfono al registrarlo."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label={`Código dictado a ${callerId.e164}`}
          hint="Seis dígitos. Caduca 15 minutos después del registro y admite 5 intentos; agotados, hay que registrar el número otra vez."
          error={error ?? undefined}
        >
          <input
            className={`${inputClass} max-w-[12rem] font-mono tracking-widest`}
            required
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || code.length !== 6}>
            {submitting ? "Verificando…" : "Verificar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
